import { useMutation, MutationResult, ExecutionResult } from 'react-apollo';
import { Enums } from '@letsdothis/tracking';

import { useAuthService } from '@/contexts/authService';
import getMutationUrl from '@/routes/routes/helpers/getMutationUrl';
import history from '@/routes/routes/helpers/history';
import { useTracking } from '@/tracking';
import { ActivityReaction, ActivityType } from '@/types/user';
import { Logger } from '@utils/logger';
import { getRelativeUrl } from '@utils/routing';

import { CommentActivityMutation } from '../gql';
import { Activity } from '../types';
import useUser from './useUser';

type Success = { commentOnActivity: ActivityReaction };

const useReply = ({
  eventId,
  activity,
  activityType,
  contentCapturedContentType,
  loginReturnUrl,
}: {
  eventId: string;
  activity: Activity;
  activityType: ActivityType;
  contentCapturedContentType: Enums.ContentCapturedContentType;
  // Used only for when url state doesn't encapsulate page state
  loginReturnUrl?: string;
}): [
  (text: string) => Promise<ExecutionResult<Success> | null | undefined>,
  MutationResult<Success>,
] => {
  const authService = useAuthService();
  const { user: userData } = useUser();
  const { contentCaptured, source: contentCapturedSource } = useTracking();
  const [addCommentActivity, mutationState] = useMutation(
    CommentActivityMutation,
  );
  const activityId = activity.id;
  const activityPrimaryIdentityId = activity?.user?.primaryIdentityId;
  const reactions = activity.reactions;
  const isUserLoggedIn = authService.loggedIn();
  const loggedInUserId = authService.getUserCookie();
  const reply = async (text: string) => {
    const hasPrimaryIdentityId = userData?.primaryIdentityId;
    const variables = {
      activityId,
      activityType,
      activityPrimaryIdentityId,
      eventId,
      text,
    };

    if (!isUserLoggedIn || !loggedInUserId || !hasPrimaryIdentityId) {
      const mutatorVariables = {
        ...variables,
        returnUrl:
          loginReturnUrl || getRelativeUrl(__CLIENT__ && window.location),
        contentCapturedContentType,
        contentCapturedSource,
      };
      history.replace(getMutationUrl('/comment-activity', mutatorVariables));
      return;
    }
    const user = { ...userData, id: userData?.primaryIdentityId };
    const reactionsId = `${activityType}::${activityId}`;

    try {
      const res = await addCommentActivity({
        variables,
        optimisticResponse: {
          __typename: 'Mutation',
          commentOnActivity: {
            id: reactionsId,
            __typename: 'ActivityReaction',
            likes: reactions?.likes || [],
            comments: (reactions?.comments || [])
              .concat({
                __typename: 'ActivityComment',
                id: `temp_${Date.now()}_${Math.random()}`,
                createdAt: new Date(),
                text,
                user,
              })
              .map((comment) => {
                return {
                  reactions: {
                    __typename: 'ActivityReaction',
                    id: `temp_${Date.now()}_${Math.random()}`,
                    comments: [],
                    likes: [],
                  },
                  ...comment,
                };
              }),
          },
        },
      });
      contentCaptured(contentCapturedContentType);

      return res;
    } catch (error) {
      Logger.error('[ERROR uploading new reply]: ', error);
      return null;
    }
  };

  return [reply, mutationState];
};

export default useReply;
